import {
  queryOptions,
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import { z } from 'zod';

import { apiFetch } from '@/lib/api-fetch.js';

/**
 * Client for workspace memory (`cti-workspace-service`, reached through the gateway at
 * `/v1/workspaces/:workspaceId/memory`). Two documents live behind it: one `shared` document per
 * workspace and the caller's own `personal` one. `scope=personal` always means the caller's — no route
 * takes an owner, so nobody else's personal memory can be asked for.
 *
 * REST is snake_case on the wire and the SPA is camelCase; the mapping happens here and nowhere else.
 * The service validates with `ValidationPipe({ whitelist: true })`, which drops an undeclared key with
 * no error and no log, so every request body below is asserted by `memory.test.ts` rather than trusted
 * to the types.
 *
 * `type`, `status`, `visibility`, `operation`, `origin` and `source_kind` are OPEN unions — a newer
 * service may send a value this build has never heard of, so they stay plain strings here and every
 * lookup that keys on them falls back to a safe default (CLAUDE.md).
 */

const API_BASE = '/v1/workspaces';

/** Which document a call is about. One shared document per workspace, one personal per member. */
export const MEMORY_SCOPES = ['shared', 'personal'] as const;

/** Which document a call is about (see {@link MEMORY_SCOPES}). */
export type MemoryScope = (typeof MEMORY_SCOPES)[number];

/** The block types this build knows how to render. Open — see the module note. */
export const MEMORY_BLOCK_TYPES = [
  'current_context',
  'decision',
  'fact',
  'convention',
  'open_thread',
  'summary',
  'preference',
  'role',
] as const;

/** A block type this build knows (see {@link MEMORY_BLOCK_TYPES}). */
export type MemoryBlockType = (typeof MEMORY_BLOCK_TYPES)[number];

/**
 * The two block types a revision condition belongs on (canon §3.2). The service refuses one on any
 * other type rather than dropping it, so the control does not exist there either.
 */
export const MEMORY_CONDITION_BLOCK_TYPES = ['decision', 'fact'] as const;

/**
 * How a revision condition is tested. `queryable` is answered by a system through one of the caller's
 * connections; `assertable` is a reminder for a person, and nothing ever runs on it (canon §11).
 */
export const MEMORY_REVISES_IF_KINDS = ['queryable', 'assertable'] as const;

/** How a revision condition is tested (see {@link MEMORY_REVISES_IF_KINDS}). */
export type MemoryRevisesIfKind = (typeof MEMORY_REVISES_IF_KINDS)[number];

/**
 * How often a condition is looked at again. The interval means two different things by kind, and the
 * asymmetry is deliberate (canon §11): on a `queryable` condition it is the schedule a check run
 * follows, on an `assertable` one it is when a person is reminded to look again.
 */
export const MEMORY_RECHECK_INTERVALS = [
  '1d',
  '7d',
  '30d',
  'on_demand',
] as const;

/** How often a condition is looked at again (see {@link MEMORY_RECHECK_INTERVALS}). */
export type MemoryRecheckInterval = (typeof MEMORY_RECHECK_INTERVALS)[number];

/**
 * How one run of a revision condition ended (canon §3.5). `failed` is never read as "nothing changed":
 * the whole reason runs are kept is that "we checked and it matched" and "we could not reach the system
 * for four weeks" stay two different answers.
 */
export const MEMORY_CHECK_OUTCOMES = ['matched', 'differs', 'failed'] as const;

/** How one run ended (see {@link MEMORY_CHECK_OUTCOMES}). */
export type MemoryCheckOutcome = (typeof MEMORY_CHECK_OUTCOMES)[number];

/** Why a run failed, when it did. */
export const MEMORY_CHECK_FAILURE_KINDS = [
  'timeout',
  'no_credential',
  'unauthorized',
  'not_found',
  'transport',
  'unknown',
] as const;

/** Why a run failed (see {@link MEMORY_CHECK_FAILURE_KINDS}). */
export type MemoryCheckFailureKind =
  (typeof MEMORY_CHECK_FAILURE_KINDS)[number];

const MemoryDocumentWireSchema = z.object({
  id: z.string().nullable(),
  scope: z.enum(MEMORY_SCOPES),
  owner_user_id: z.string().nullable().default(null),
  title: z.string().nullable().default(null),
  markdown: z.string(),
  version: z.number(),
  character_count: z.number(),
  status: z.string(),
  updated_at: z.string().nullable().default(null),
});

/**
 * A block's revision condition, as the block view answers it — and deliberately the exact object
 * `PATCH /blocks/:blockId` accepts back, so a form reads it, changes one field and sends it home.
 *
 * `recheck_interval` lives IN here rather than beside the block: an interval means nothing without a
 * condition to look at again, and the patch body carries no field of its own for it.
 */
const MemoryRevisesIfWireSchema = z.object({
  kind: z.string(),
  check: z.string().nullable().default(null),
  connection_id: z.string().nullable().default(null),
  snapshot: z.string().nullable().default(null),
  recheck_interval: z.string().nullable().default(null),
});

/**
 * The condition a proposal was drafted with — the block's own object minus `snapshot`, because nothing
 * has ever run against a condition nobody has accepted yet. Omitted from the block schema rather than
 * written out again, so the two can never describe the same four fields differently.
 */
const MemoryProposedRevisesIfWireSchema = MemoryRevisesIfWireSchema.omit({
  snapshot: true,
});

/**
 * One run of a block's revision condition on the wire (canon §3.5).
 *
 * `outcome` and `failure_kind` stay plain strings for the same reason every other vocabulary value here
 * does: a newer service can send a word this build has never heard of, and a lookup that indexes blindly
 * would render `undefined` at somebody.
 *
 * `answer_digest` is a hash or a short normalised form and never the answer itself — a customer system's
 * full answer may hold what must not be in memory. The interface never shows it; it is read only so a
 * caller can tell one run's baseline from another's.
 */
const MemoryCheckRunWireSchema = z.object({
  id: z.string(),
  block_id: z.string(),
  outcome: z.string(),
  failure_kind: z.string().nullable().default(null),
  connection_id: z.string().nullable().default(null),
  executed_as_user_id: z.string().nullable().default(null),
  answer_digest: z.string().nullable().default(null),
  started_at: z.string(),
  finished_at: z.string().nullable().default(null),
});

const MemoryBlockWireSchema = z.object({
  id: z.string(),
  document_id: z.string(),
  type: z.string(),
  /** The block's own optimistic lock. Defaulted for a service older than the field, never absent. */
  version: z.number().default(1),
  section: z.string().nullable().default(null),
  ordinal: z.number(),
  content_markdown: z.string(),
  visibility: z.string(),
  status: z.string(),
  confidence: z.string().nullable().default(null),
  source_kind: z.string(),
  source_conversation_id: z.string().nullable().default(null),
  source_message_ids: z.array(z.string()).nullable().default(null),
  created_by_user_id: z.string().nullable().default(null),
  confirmed_by_user_id: z.string().nullable().default(null),
  supersedes_block_id: z.string().nullable().default(null),
  revises_if: MemoryRevisesIfWireSchema.nullable().default(null),
  next_check_at: z.string().nullable().default(null),
  last_check_run_id: z.string().nullable().default(null),
  last_check_run: MemoryCheckRunWireSchema.nullable().default(null),
  is_note: z.boolean().optional(),
  created_at: z.string(),
  updated_at: z.string(),
});

const MemoryProposalWireSchema = z.object({
  id: z.string(),
  workspace_id: z.string(),
  target_document_id: z.string(),
  target_block_id: z.string().nullable().default(null),
  operation: z.string(),
  proposed_type: z.string().nullable().default(null),
  proposed_section: z.string().nullable().default(null),
  proposed_content_markdown: z.string().nullable().default(null),
  proposed_visibility: z.string().nullable().default(null),
  proposed_revises_if:
    MemoryProposedRevisesIfWireSchema.nullable().default(null),
  base_document_version: z.number(),
  confidence: z.string(),
  reason: z.string(),
  origin: z.string(),
  source_conversation_id: z.string().nullable().default(null),
  source_message_ids: z.array(z.string()).nullable().default(null),
  proposed_by_user_id: z.string().nullable().default(null),
  required_approver: z.string(),
  status: z.string(),
  seen_at: z.string().nullable().default(null),
  reviewed_by_user_id: z.string().nullable().default(null),
  reviewed_at: z.string().nullable().default(null),
  expires_at: z.string().nullable().default(null),
  created_at: z.string(),
  updated_at: z.string(),
});

const MemoryRevisionWireSchema = z.object({
  id: z.string(),
  version: z.number(),
  operation_summary: z.string(),
  actor_user_id: z.string().nullable().default(null),
  candidate_id: z.string().nullable().default(null),
  source_conversation_id: z.string().nullable().default(null),
  source_kind: z.string(),
  created_at: z.string(),
  /**
   * Whether an undo onto this version restores the entries or only their text. Defaults to `false`, so a
   * service older than the field is read as "text only" — the cautious side, where the interface explains
   * the limit instead of offering an undo that could only refuse.
   */
  restores_state: z.boolean().default(false),
  /**
   * Whether this version deleted a record for good. Defaults to `false` on a service older than the
   * field: claiming a deletion that did not happen would take away an undo that works, and the click
   * still refuses safely where it did.
   */
  deleted_records: z.boolean().default(false),
});

/** One memory document as the SPA consumes it. `version` is the optimistic lock a save sends back. */
export interface MemoryDocument {
  id: string | null;
  scope: MemoryScope;
  ownerUserId: string | null;
  title: string | null;
  markdown: string;
  version: number;
  characterCount: number;
  status: string;
  updatedAt: string | null;
}

/**
 * A block's revision condition as the SPA consumes it (canon §11).
 *
 * `kind` and `recheckInterval` stay plain strings on the read side — they are OPEN unions like every
 * other vocabulary value here, so a newer service's word renders rather than crashing a lookup.
 */
export interface MemoryRevisionCondition {
  kind: string;
  check: string | null;
  connectionId: string | null;
  /** What the last check run saw. The server's alone — no form ever sends it. */
  snapshot: string | null;
  recheckInterval: string | null;
}

/**
 * The revision condition a proposal proposes (canon §11). No `snapshot`: nothing runs on a condition
 * until the proposal carrying it is accepted, which is also why accepting one starts a schedule.
 */
export type MemoryProposedRevisionCondition = Omit<
  MemoryRevisionCondition,
  'snapshot'
>;

/**
 * One run of a block's revision condition as the SPA consumes it (canon §3.5).
 *
 * `outcome` and `failureKind` are OPEN unions and stay strings — the card reads them through lookups
 * that fall back rather than through the type.
 *
 * `executedAsUserId` is the **internal identity id** (canon §6), the same id space as
 * `workspace_member.user_id`, which is what lets the member directory put a name to it. It is never
 * rendered as it stands: a uuid at a person names nobody.
 */
export interface MemoryCheckRun {
  id: string;
  blockId: string;
  outcome: string;
  failureKind: string | null;
  connectionId: string | null;
  executedAsUserId: string | null;
  /** A hash of the answer, never the answer. Read but not shown — see the wire schema. */
  answerDigest: string | null;
  startedAt: string;
  finishedAt: string | null;
}

/** One block of memory as the SPA consumes it. */
export interface MemoryBlock {
  id: string;
  documentId: string;
  /** Which document it came out of. Stamped from the request, not the wire — the row omits it. */
  scope: MemoryScope;
  /**
   * The version an edit of this one memory is built on, counted from 1 and moved by every change to the
   * block — an archive and a visibility change included, so a stale editor loses that race too.
   *
   * Its own lock rather than the document's: two people editing two different memories must not collide,
   * which is exactly what a document-wide version made them do.
   */
  version: number;
  type: string;
  section: string | null;
  ordinal: number;
  contentMarkdown: string;
  visibility: string;
  status: string;
  confidence: string | null;
  sourceKind: string;
  sourceConversationId: string | null;
  sourceMessageIds: string[] | null;
  createdByUserId: string | null;
  confirmedByUserId: string | null;
  supersedesBlockId: string | null;
  /** When this block is looked at again, or null. Its own condition carries the interval. */
  revisesIf: MemoryRevisionCondition | null;
  /** When the next `queryable` run is due. The server computes it; an assertable block has none. */
  nextCheckAt: string | null;
  lastCheckRunId: string | null;
  /**
   * The last run of this block's condition, resolved by the server rather than left as an id — the card
   * shows the outcome, the date and whose connection answered, and none of that is in an id.
   *
   * Null when nothing has ever run against this block, which is a different state from a run that
   * matched and has to read differently on screen (canon §11).
   */
  lastCheckRun: MemoryCheckRun | null;
  /** True for a decision carrying no revision condition — shown as a note rather than a decision. */
  isNote: boolean;
  createdAt: string;
  updatedAt: string;
}

/** One proposal as the SPA consumes it — the card in the chat and the row in the inbox. */
export interface MemoryProposal {
  id: string;
  workspaceId: string;
  targetDocumentId: string;
  targetBlockId: string | null;
  /** Which document it targets. Stamped from the request, for the same reason as on a block. */
  scope: MemoryScope;
  operation: string;
  proposedType: string | null;
  proposedSection: string | null;
  proposedContentMarkdown: string | null;
  proposedVisibility: string | null;
  /**
   * The condition drafted for the block this proposal would add, or null when it drafted none.
   * Accepting puts it on the block as it stands — an approver may reword the text but not the
   * condition — so the card has to show it before anybody says yes.
   */
  proposedRevisesIf: MemoryProposedRevisionCondition | null;
  baseDocumentVersion: number;
  confidence: string;
  reason: string;
  origin: string;
  sourceConversationId: string | null;
  sourceMessageIds: string[] | null;
  proposedByUserId: string | null;
  requiredApprover: string;
  status: string;
  seenAt: string | null;
  reviewedByUserId: string | null;
  reviewedAt: string | null;
  expiresAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** One entry of a document's history as the SPA consumes it. */
export interface MemoryRevision {
  id: string;
  version: number;
  operationSummary: string;
  actorUserId: string | null;
  candidateId: string | null;
  /**
   * The conversation this change came out of, resolved by the service through the revision's
   * candidate. Null for a change nobody talked their way into — a manual edit, an undo, a compaction —
   * and null on a service older than the field, which is why the history row renders without it
   * rather than implying a conversation that does not exist.
   */
  sourceConversationId: string | null;
  sourceKind: string;
  createdAt: string;
  /**
   * Whether rolling back to this version puts the entries back, or only their text. A version recorded
   * before memory kept block state cannot restore a condition or a visibility, so the history offers no
   * undo onto it and says why in its place.
   */
  restoresState: boolean;
  /**
   * Whether this change destroyed a record. Nothing brings one back — the row is gone — so the history
   * offers no undo on it and says so in place of the button.
   */
  deletedRecords: boolean;
}

/**
 * Thrown by every call in this module on a non-ok response. Carries the status so a caller can branch
 * on it — a 403 on a write is the documented "you may not do this", not a broken session.
 */
export class MemoryApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'MemoryApiError';
  }
}

/**
 * The 409 the optimistic lock produces, with the document as it now stands attached.
 *
 * Its own class rather than a generic toast, because the caller has to be able to show the divergence:
 * the editor keeps the text the person typed, shows what is in memory now, and lets them merge. A
 * message alone would leave them with a failed save and no way to see what it collided with.
 *
 * `document` is null when the server answered a conflict with no document — the "already answered"
 * conflict on a proposal, which is a different collision with nothing to diff.
 */
export class MemoryVersionConflictError extends MemoryApiError {
  constructor(
    message: string,
    public readonly document: MemoryDocument | null,
  ) {
    super(409, message);
    this.name = 'MemoryVersionConflictError';
  }
}

/**
 * The 409 one memory's own lock produces, with the block as it now stands attached.
 *
 * Its own class beside {@link MemoryVersionConflictError} rather than a flag on it, because the two
 * collisions are answered differently: the document conflict offers a whole file to rebase onto, and
 * this one offers a single memory — the card shows the two texts side by side and the person picks,
 * while every other card on the screen stays editable.
 *
 * `block` is null when the server answered a conflict with no block on it — an archive that landed
 * first, say, which is a collision with nothing to diff.
 */
export class MemoryBlockVersionConflictError extends MemoryApiError {
  constructor(
    message: string,
    public readonly block: MemoryBlock | null,
  ) {
    super(409, message);
    this.name = 'MemoryBlockVersionConflictError';
  }
}

/**
 * A 403 on a READ is the platform-level signal the neighbouring modules treat it as: every member may
 * read the shared document and `scope=personal` is always the caller's own, so a refusal here means
 * the session lost access rather than the person lacking a right.
 *
 * Writes deliberately do NOT go through this. A `viewer` saving the shared document is answered 403 by
 * design (canon §4), and throwing the whole SPA onto /access-denied for it would turn an expected
 * refusal into a sign-out.
 */
function checkAccessDenied(res: Response): void {
  if (res.status === 403 && window.location.pathname !== '/access-denied') {
    window.location.replace('/access-denied');
    throw new MemoryApiError(403, 'Access denied — redirecting');
  }
}

/** Pulls the server's own explanation out of a non-ok response, or falls back to the action. */
async function errorMessage(res: Response, action: string): Promise<string> {
  const text = await res.text().catch(() => '');
  try {
    const body = JSON.parse(text) as { message?: string | string[] };
    if (Array.isArray(body.message) && body.message.length > 0) {
      return body.message.join(', ');
    }
    if (typeof body.message === 'string' && body.message) return body.message;
  } catch {
    /** Not JSON — the raw text below is the best the response offers. */
  }
  if (text.trim()) return text.trim();
  return `Failed to ${action} (${res.status})`;
}

/** A refused write, read once: the words the service used, and whatever body it put them in. */
async function readWriteFailure(
  res: Response,
  action: string,
): Promise<{
  message: string;
  parsed: { message?: string | string[]; document?: unknown; block?: unknown };
}> {
  const text = await res.text().catch(() => '');
  let parsed: {
    message?: string | string[];
    document?: unknown;
    block?: unknown;
  } = {};
  try {
    parsed = JSON.parse(text) as typeof parsed;
  } catch {
    /** Not JSON — `message` below falls back to the raw text. */
  }
  const message = Array.isArray(parsed.message)
    ? parsed.message.join(', ')
    : typeof parsed.message === 'string' && parsed.message
      ? parsed.message
      : text.trim() || `Failed to ${action} (${res.status})`;
  return { message, parsed };
}

/**
 * Turn a failed write into the right error. A 409 carrying a document is the version conflict and gets
 * its own type; every other refusal keeps the service's own words, which already name what was wrong.
 */
async function throwWriteFailure(
  res: Response,
  action: string,
): Promise<never> {
  const { message, parsed } = await readWriteFailure(res, action);
  if (res.status === 409) {
    const document = MemoryDocumentWireSchema.safeParse(parsed.document);
    throw new MemoryVersionConflictError(
      message,
      document.success ? toDocument(document.data) : null,
    );
  }
  throw new MemoryApiError(res.status, message);
}

/**
 * The same, for a write against one block. A 409 here carries the **block**, so it becomes the block
 * conflict rather than the document one — the card has a single memory to show, not a file.
 *
 * A 409 with no block on it is left as a plain refusal with the service's own words: there is nothing
 * to diff, and dressing it as a conflict would put two buttons under an empty comparison.
 */
async function throwBlockWriteFailure(
  res: Response,
  action: string,
  scope: MemoryScope,
): Promise<never> {
  const { message, parsed } = await readWriteFailure(res, action);
  if (res.status === 409) {
    const block = MemoryBlockWireSchema.safeParse(parsed.block);
    throw new MemoryBlockVersionConflictError(
      message,
      block.success ? toBlock(block.data, scope) : null,
    );
  }
  throw new MemoryApiError(res.status, message);
}

/** Content type only — `apiFetch` attaches the bearer from the renewal core. */
const JSON_HEADERS: Record<string, string> = {
  'Content-Type': 'application/json',
};

function toDocument(
  wire: z.infer<typeof MemoryDocumentWireSchema>,
): MemoryDocument {
  return {
    id: wire.id,
    scope: wire.scope,
    ownerUserId: wire.owner_user_id,
    title: wire.title,
    markdown: wire.markdown,
    version: wire.version,
    characterCount: wire.character_count,
    status: wire.status,
    updatedAt: wire.updated_at,
  };
}

/** The revision condition as the SPA consumes it, or null when the block carries none. */
function toRevisionCondition(
  wire: z.infer<typeof MemoryRevisesIfWireSchema> | null,
): MemoryRevisionCondition | null {
  if (wire === null) return null;
  return {
    kind: wire.kind,
    check: wire.check,
    connectionId: wire.connection_id,
    snapshot: wire.snapshot,
    recheckInterval: wire.recheck_interval,
  };
}

/** The proposed condition as the SPA consumes it, or null when the proposal drafted none. */
function toProposedRevisionCondition(
  wire: z.infer<typeof MemoryProposedRevisesIfWireSchema> | null,
): MemoryProposedRevisionCondition | null {
  if (wire === null) return null;
  return {
    kind: wire.kind,
    check: wire.check,
    connectionId: wire.connection_id,
    recheckInterval: wire.recheck_interval,
  };
}

/** One run as the SPA consumes it. */
function toCheckRun(
  wire: z.infer<typeof MemoryCheckRunWireSchema>,
): MemoryCheckRun {
  return {
    id: wire.id,
    blockId: wire.block_id,
    outcome: wire.outcome,
    failureKind: wire.failure_kind,
    connectionId: wire.connection_id,
    executedAsUserId: wire.executed_as_user_id,
    answerDigest: wire.answer_digest,
    startedAt: wire.started_at,
    finishedAt: wire.finished_at,
  };
}

function toBlock(
  wire: z.infer<typeof MemoryBlockWireSchema>,
  scope: MemoryScope,
): MemoryBlock {
  return {
    id: wire.id,
    documentId: wire.document_id,
    scope,
    version: wire.version,
    type: wire.type,
    section: wire.section,
    ordinal: wire.ordinal,
    contentMarkdown: wire.content_markdown,
    visibility: wire.visibility,
    status: wire.status,
    confidence: wire.confidence,
    sourceKind: wire.source_kind,
    sourceConversationId: wire.source_conversation_id,
    sourceMessageIds: wire.source_message_ids,
    createdByUserId: wire.created_by_user_id,
    confirmedByUserId: wire.confirmed_by_user_id,
    supersedesBlockId: wire.supersedes_block_id,
    revisesIf: toRevisionCondition(wire.revises_if),
    nextCheckAt: wire.next_check_at,
    lastCheckRunId: wire.last_check_run_id,
    lastCheckRun:
      wire.last_check_run === null ? null : toCheckRun(wire.last_check_run),
    isNote: wire.is_note === true,
    createdAt: wire.created_at,
    updatedAt: wire.updated_at,
  };
}

function toProposal(
  wire: z.infer<typeof MemoryProposalWireSchema>,
  scope: MemoryScope,
): MemoryProposal {
  return {
    id: wire.id,
    workspaceId: wire.workspace_id,
    targetDocumentId: wire.target_document_id,
    targetBlockId: wire.target_block_id,
    scope,
    operation: wire.operation,
    proposedType: wire.proposed_type,
    proposedSection: wire.proposed_section,
    proposedContentMarkdown: wire.proposed_content_markdown,
    proposedVisibility: wire.proposed_visibility,
    proposedRevisesIf: toProposedRevisionCondition(wire.proposed_revises_if),
    baseDocumentVersion: wire.base_document_version,
    confidence: wire.confidence,
    reason: wire.reason,
    origin: wire.origin,
    sourceConversationId: wire.source_conversation_id,
    sourceMessageIds: wire.source_message_ids,
    proposedByUserId: wire.proposed_by_user_id,
    requiredApprover: wire.required_approver,
    status: wire.status,
    seenAt: wire.seen_at,
    reviewedByUserId: wire.reviewed_by_user_id,
    reviewedAt: wire.reviewed_at,
    expiresAt: wire.expires_at,
    createdAt: wire.created_at,
    updatedAt: wire.updated_at,
  };
}

function toRevision(
  wire: z.infer<typeof MemoryRevisionWireSchema>,
): MemoryRevision {
  return {
    id: wire.id,
    version: wire.version,
    operationSummary: wire.operation_summary,
    actorUserId: wire.actor_user_id,
    candidateId: wire.candidate_id,
    sourceConversationId: wire.source_conversation_id,
    sourceKind: wire.source_kind,
    createdAt: wire.created_at,
    restoresState: wire.restores_state,
    deletedRecords: wire.deleted_records,
  };
}

function memoryPath(workspaceId: string, suffix = ''): string {
  return `${API_BASE}/${encodeURIComponent(workspaceId)}/memory${suffix}`;
}

/** One memory document. A document nobody has written yet answers `version: 0` and empty Markdown. */
export async function fetchMemoryDocument(
  workspaceId: string,
  scope: MemoryScope,
): Promise<MemoryDocument> {
  const res = await apiFetch(`${memoryPath(workspaceId)}?scope=${scope}`, {
    headers: JSON_HEADERS,
  });
  checkAccessDenied(res);
  if (!res.ok) {
    throw new MemoryApiError(
      res.status,
      await errorMessage(res, 'load memory'),
    );
  }
  return toDocument(MemoryDocumentWireSchema.parse(await res.json()));
}

/** The document's blocks, in render order. What the map and the search list are built from. */
export async function fetchMemoryBlocks(
  workspaceId: string,
  scope: MemoryScope,
): Promise<MemoryBlock[]> {
  const res = await apiFetch(
    `${memoryPath(workspaceId, '/blocks')}?scope=${scope}`,
    {
      headers: JSON_HEADERS,
    },
  );
  checkAccessDenied(res);
  if (!res.ok) {
    throw new MemoryApiError(
      res.status,
      await errorMessage(res, 'load memory blocks'),
    );
  }
  const body = z
    .object({ blocks: z.array(MemoryBlockWireSchema) })
    .parse(await res.json());
  return body.blocks.map((row) => toBlock(row, scope));
}

/**
 * One block's check runs, newest first — the four weeks of silence a single "last check" line cannot
 * show. Gated exactly like reading the block it belongs to.
 *
 * The service trims old runs but never drops the newest one that actually answered, so the list a
 * failed block answers with still carries the date of the last successful check however long ago it
 * was.
 */
export async function fetchMemoryCheckRuns(
  workspaceId: string,
  blockId: string,
  limit?: number,
): Promise<MemoryCheckRun[]> {
  const query = new URLSearchParams();
  if (limit !== undefined) query.set('limit', String(limit));
  const suffix = query.toString() === '' ? '' : `?${query.toString()}`;
  const res = await apiFetch(
    `${memoryPath(workspaceId, `/blocks/${encodeURIComponent(blockId)}/check-runs`)}${suffix}`,
    { headers: JSON_HEADERS },
  );
  checkAccessDenied(res);
  if (!res.ok) {
    throw new MemoryApiError(
      res.status,
      await errorMessage(res, 'load the check history'),
    );
  }
  const body = z
    .object({ check_runs: z.array(MemoryCheckRunWireSchema) })
    .parse(await res.json());
  return body.check_runs.map(toCheckRun);
}

/** The document's history, newest first. Every member may read the shared document's, in full. */
export async function fetchMemoryRevisions(
  workspaceId: string,
  scope: MemoryScope,
  limit?: number,
): Promise<MemoryRevision[]> {
  const query = new URLSearchParams({ scope });
  if (limit !== undefined) query.set('limit', String(limit));
  const res = await apiFetch(
    `${memoryPath(workspaceId, '/revisions')}?${query.toString()}`,
    {
      headers: JSON_HEADERS,
    },
  );
  checkAccessDenied(res);
  if (!res.ok) {
    throw new MemoryApiError(
      res.status,
      await errorMessage(res, 'load memory history'),
    );
  }
  const body = z
    .object({ revisions: z.array(MemoryRevisionWireSchema) })
    .parse(await res.json());
  return body.revisions.map(toRevision);
}

/** The proposals on one document, optionally narrowed by status and origin. */
export async function fetchMemoryProposals(
  workspaceId: string,
  scope: MemoryScope,
  filters: { status?: string; origin?: string } = {},
): Promise<MemoryProposal[]> {
  const query = new URLSearchParams({ scope });
  if (filters.status !== undefined) query.set('status', filters.status);
  if (filters.origin !== undefined) query.set('origin', filters.origin);
  const res = await apiFetch(
    `${memoryPath(workspaceId, '/proposals')}?${query.toString()}`,
    {
      headers: JSON_HEADERS,
    },
  );
  checkAccessDenied(res);
  if (!res.ok) {
    throw new MemoryApiError(
      res.status,
      await errorMessage(res, 'load memory suggestions'),
    );
  }
  const body = z
    .object({ proposals: z.array(MemoryProposalWireSchema) })
    .parse(await res.json());
  return body.proposals.map((row) => toProposal(row, scope));
}

/**
 * Every pending proposal waiting on this person, across both documents — what the inbox lists and what
 * the sidebar counts. Two calls because the wire takes one scope at a time; newest first.
 */
export async function fetchPendingMemoryProposals(
  workspaceId: string,
): Promise<MemoryProposal[]> {
  const [shared, personal] = await Promise.all([
    fetchMemoryProposals(workspaceId, 'shared', { status: 'pending' }),
    fetchMemoryProposals(workspaceId, 'personal', { status: 'pending' }),
  ]);
  return [...shared, ...personal].sort((left, right) =>
    right.createdAt.localeCompare(left.createdAt),
  );
}

/** What a save sends. `baseVersion` is the lock; zero is legal and means "there was nothing here". */
export interface SaveMemoryDocumentInput {
  scope: MemoryScope;
  markdown: string;
  baseVersion: number;
}

/**
 * Save a whole document a person edited. The Markdown goes up entire and the service decomposes it
 * into operations over blocks, so an untouched paragraph keeps the block it always had.
 *
 * A stale `base_version` answers 409 with the current document — see {@link MemoryVersionConflictError}.
 */
export async function saveMemoryDocument(
  workspaceId: string,
  input: SaveMemoryDocumentInput,
): Promise<MemoryDocument> {
  const res = await apiFetch(memoryPath(workspaceId, '/document'), {
    method: 'PUT',
    headers: JSON_HEADERS,
    body: JSON.stringify({
      scope: input.scope,
      markdown: input.markdown,
      base_version: input.baseVersion,
    }),
  });
  if (!res.ok) await throwWriteFailure(res, 'save memory');
  return toDocument(MemoryDocumentWireSchema.parse(await res.json()));
}

/**
 * The revision condition a form sends (canon §11). `kind` is the one required field; a `queryable`
 * condition needs both the check and the connection, and the service refuses one that names either
 * without the other rather than quietly downgrading it to a reminder.
 *
 * `recheckInterval` is a plain string for the reason `type` is one on {@link PatchMemoryBlockInput}: the
 * service owns the vocabulary and ships a new interval before this build knows it, so narrowing the way
 * OUT to what this build knows would make a condition on a newer interval impossible to re-save
 * unchanged. The service validates the word it gets and refuses one it does not know.
 */
export interface MemoryRevisionConditionInput {
  kind: MemoryRevisesIfKind;
  check?: string;
  connectionId?: string;
  recheckInterval?: string;
}

/**
 * What a patch changes on one block: its text, its type, its section, its visibility, its status, or
 * its revision condition.
 *
 * `revisesIf` and `section` each carry **three** states and they are all distinct on the wire:
 * `undefined` leaves the field alone and sends no key at all, `null` sends an explicit null and clears
 * it — which brings `is_note` back on a decision, and takes a memory out from under its heading — and a
 * value replaces it.
 *
 * `baseVersion` is the block's own optimistic lock and is required by the service whenever the text,
 * the type or the section is being changed. The three fields that do not compete for the text —
 * visibility, status and the condition — need none, so a card can archive a memory it never read; a
 * body stating the version without one of the three is a 422, so it is left out rather than sent.
 * Those three still move the block's `version` on the server, so a stale editor loses that race too.
 *
 * `type` is a plain string on purpose. It is an OPEN union on the way in, and narrowing it here would
 * make a memory of a type this build has never heard of impossible to re-save unchanged.
 *
 * `scope` never reaches the body. It is which document the answering block belongs to, stamped onto it
 * the same way a listed block gets one, because the row does not carry it.
 */
export interface PatchMemoryBlockInput {
  scope: MemoryScope;
  contentMarkdown?: string;
  type?: string;
  section?: string | null;
  baseVersion?: number;
  visibility?: string;
  status?: string;
  revisesIf?: MemoryRevisionConditionInput | null;
}

/** What a patch answers: the block as it now stands, and the document the change moved. */
export interface PatchedMemoryBlock {
  block: MemoryBlock;
  document: MemoryDocument;
}

/**
 * The condition in the snake_case both DTOs declare — the block patch's `revises_if` and the proposal's
 * `proposed_revises_if` hold the same object, so one mapping serves both. `connection_id` arriving as
 * `connectionId` is dropped by the whitelist with no error at all, so `memory.test.ts` asserts these
 * keys.
 *
 * A blank check or connection is omitted rather than sent as an empty string: an empty field is a
 * field nobody filled, and the service reads it as missing anyway.
 */
function revisesIfBody(
  condition: MemoryRevisionConditionInput,
): Record<string, unknown> {
  const body: Record<string, unknown> = { kind: condition.kind };
  if (condition.check !== undefined && condition.check.trim() !== '') {
    body.check = condition.check.trim();
  }
  if (
    condition.connectionId !== undefined &&
    condition.connectionId.trim() !== ''
  ) {
    body.connection_id = condition.connectionId.trim();
  }
  if (condition.recheckInterval !== undefined) {
    body.recheck_interval = condition.recheckInterval;
  }
  return body;
}

/**
 * Change one block in place. The block's owner or an editor and above — which is what lets a `viewer`
 * who brought a fact put the revision condition on it afterwards, instead of filing a proposal against
 * their own block (canon §4).
 *
 * A refused condition comes back 422 with the reason the service worked out, and that reason is what
 * {@link MemoryApiError} carries: it names what is missing, which a generic failure could not. A stale
 * `baseVersion` comes back 409 with the block — see {@link MemoryBlockVersionConflictError}.
 */
export async function patchMemoryBlock(
  workspaceId: string,
  blockId: string,
  input: PatchMemoryBlockInput,
): Promise<PatchedMemoryBlock> {
  const body: Record<string, unknown> = {};
  if (input.contentMarkdown !== undefined) {
    body.content_markdown = input.contentMarkdown;
  }
  if (input.type !== undefined) body.type = input.type;
  if (input.section !== undefined) body.section = input.section;
  /**
   * The lock rides the fields it locks, and nothing else. A body stating `base_version` with none of
   * the three is answered **422**, so a caller that sends the version defensively would break the
   * visibility toggle, the archive and the condition editor — three writes that have never needed it.
   */
  const locks =
    input.contentMarkdown !== undefined ||
    input.type !== undefined ||
    input.section !== undefined;
  if (locks && input.baseVersion !== undefined) {
    body.base_version = input.baseVersion;
  }
  if (input.visibility !== undefined) body.visibility = input.visibility;
  if (input.status !== undefined) body.status = input.status;
  if (input.revisesIf !== undefined) {
    body.revises_if =
      input.revisesIf === null ? null : revisesIfBody(input.revisesIf);
  }
  const res = await apiFetch(
    memoryPath(workspaceId, `/blocks/${encodeURIComponent(blockId)}`),
    { method: 'PATCH', headers: JSON_HEADERS, body: JSON.stringify(body) },
  );
  if (!res.ok) {
    await throwBlockWriteFailure(res, 'change this memory', input.scope);
  }
  const answered = z
    .object({
      block: MemoryBlockWireSchema,
      document: MemoryDocumentWireSchema,
    })
    .parse(await res.json());
  return {
    block: toBlock(answered.block, input.scope),
    document: toDocument(answered.document),
  };
}

/**
 * What a proposal carries. Any member may file one, a `viewer` included — only answering is gated.
 *
 * `revisesIf` is the revision condition drafted for the block an `add` would create (canon §11). It
 * travels with the proposal and lands on the block whoever accepts it produces, with the due date
 * computed then — so a card waiting for somebody else's approval already carries its condition, and the
 * approver may edit the text but not the condition. The route refuses one at filing time when the
 * operation is not an `add`, when the type may not carry a condition, or when the condition itself does
 * not hold up, so a form has to show the 422 rather than swallow it.
 */
export interface CreateMemoryProposalInput {
  scope: MemoryScope;
  operation: 'add' | 'update' | 'delete' | 'archive' | 'change_visibility';
  type?: MemoryBlockType;
  section?: string;
  content?: string;
  targetBlockId?: string;
  reason: string;
  sourceConversationId?: string;
  revisesIf?: MemoryRevisionConditionInput;
}

/**
 * File a proposal. The author is the verified session, never the body.
 *
 * The answer says which of canon §8.1's two lanes the server took — see {@link FiledMemoryProposal}. A
 * `personal` one is already applied when it comes back; a `shared` one is waiting.
 */
export async function createMemoryProposal(
  workspaceId: string,
  input: CreateMemoryProposalInput,
): Promise<FiledMemoryProposal> {
  const body: Record<string, unknown> = {
    scope: input.scope,
    operation: input.operation,
    reason: input.reason,
    origin: 'manual_edit',
  };
  if (input.type !== undefined) body.type = input.type;
  if (input.section !== undefined && input.section !== '') {
    body.section = input.section;
  }
  if (input.content !== undefined) body.content = input.content;
  if (input.targetBlockId !== undefined)
    body.target_block_id = input.targetBlockId;
  if (input.sourceConversationId !== undefined) {
    body.source_conversation_id = input.sourceConversationId;
  }
  if (input.revisesIf !== undefined) {
    body.proposed_revises_if = revisesIfBody(input.revisesIf);
  }
  const res = await apiFetch(memoryPath(workspaceId, '/proposals'), {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(body),
  });
  if (!res.ok) await throwWriteFailure(res, 'file a memory suggestion');
  const filed = z
    .object({
      proposal: MemoryProposalWireSchema,
      document: MemoryDocumentWireSchema.nullable(),
    })
    .parse(await res.json());
  return {
    proposal: toProposal(filed.proposal, input.scope),
    document: filed.document === null ? null : toDocument(filed.document),
  };
}

/**
 * What filing one answers: the proposal, and the document **when the same call already applied it**.
 *
 * Two lanes, decided by the server and never by the caller (canon §8.1). A `personal` entry lands in the
 * document only its author can read, so it is applied in this same call and comes back `applied` with the
 * document it moved. A `shared` one always waits for a person to say yes — whoever filed it, owner
 * included — and comes back `pending` with `document: null`.
 *
 * Which happened is read off `proposal.status`, not guessed from the scope: a caller that assumes and
 * then accepts an already-applied proposal is answered 409.
 */
export interface FiledMemoryProposal {
  proposal: MemoryProposal;
  /** The document the immediate lane moved, or null when the proposal is waiting for an answer. */
  document: MemoryDocument | null;
}

/** What accepting one proposal answers: the proposal as it now stands and the document it changed. */
export interface AcceptedMemoryProposal {
  proposal: MemoryProposal;
  document: MemoryDocument;
}

/** Say yes, optionally with an edit made in the moment. */
export async function acceptMemoryProposal(
  workspaceId: string,
  proposalId: string,
  scope: MemoryScope,
  content?: string,
): Promise<AcceptedMemoryProposal> {
  const res = await apiFetch(
    memoryPath(
      workspaceId,
      `/proposals/${encodeURIComponent(proposalId)}/accept`,
    ),
    {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(content === undefined ? {} : { content }),
    },
  );
  if (!res.ok) await throwWriteFailure(res, 'accept this suggestion');
  const body = z
    .object({
      proposal: MemoryProposalWireSchema,
      document: MemoryDocumentWireSchema,
    })
    .parse(await res.json());
  return {
    proposal: toProposal(body.proposal, scope),
    document: toDocument(body.document),
  };
}

/** Say no. Answered, recorded, and the document untouched. */
export async function rejectMemoryProposal(
  workspaceId: string,
  proposalId: string,
  scope: MemoryScope,
): Promise<MemoryProposal> {
  const res = await apiFetch(
    memoryPath(
      workspaceId,
      `/proposals/${encodeURIComponent(proposalId)}/reject`,
    ),
    { method: 'POST', headers: JSON_HEADERS },
  );
  if (!res.ok) await throwWriteFailure(res, 'reject this suggestion');
  return toProposal(MemoryProposalWireSchema.parse(await res.json()), scope);
}

/**
 * Note that the card was seen. Fire-and-forget: an unreported sighting reads as never seen, which is
 * the lenient answer and costs the person nothing, so nothing on screen waits for it.
 */
export async function markMemoryProposalSeen(
  workspaceId: string,
  proposalId: string,
): Promise<void> {
  try {
    await apiFetch(
      memoryPath(
        workspaceId,
        `/proposals/${encodeURIComponent(proposalId)}/seen`,
      ),
      { method: 'POST', headers: JSON_HEADERS },
    );
  } catch {
    /** See the note above: a lost sighting is the harmless direction to fail in. */
  }
}

/**
 * Note that the card was opened — expanded to be read. This, not the sighting above, is what the
 * extraction gate's silence ladder counts from: a sighting is the one-line card crossing the viewport
 * 15-25 seconds after the answer, when the person is usually looking elsewhere, and charging silence for
 * three of those muted people for a month over cards they never noticed. Fire-and-forget like the
 * sighting: an unreported open reads as never opened, which costs the person nothing.
 */
export async function markMemoryProposalOpened(
  workspaceId: string,
  proposalId: string,
): Promise<void> {
  try {
    await apiFetch(
      memoryPath(
        workspaceId,
        `/proposals/${encodeURIComponent(proposalId)}/opened`,
      ),
      { method: 'POST', headers: JSON_HEADERS },
    );
  } catch {
    /** A lost open is the lenient direction to fail in, exactly like a lost sighting. */
  }
}

/**
 * Put the document back the way one revision left it, by writing a new revision on top.
 *
 * `restoreRevisionId` names the state the document ends up in — the snapshot that revision *produced*,
 * not the state it changed. So undoing a change means passing the revision **before** it: restore
 * version 6 to undo the change recorded as version 7. Passing the id of the change a person wants gone
 * restores the state that change already produced, which does nothing at all.
 */
export async function rollbackMemoryRevision(
  workspaceId: string,
  restoreRevisionId: string,
): Promise<MemoryDocument> {
  const res = await apiFetch(
    memoryPath(
      workspaceId,
      `/revisions/${encodeURIComponent(restoreRevisionId)}/rollback`,
    ),
    { method: 'POST', headers: JSON_HEADERS },
  );
  if (!res.ok) await throwWriteFailure(res, 'undo this change');
  return toDocument(MemoryDocumentWireSchema.parse(await res.json()));
}

/** Cache keys for workspace memory reads and the shared dismissal operation. */
export const memoryKeys = {
  all: (workspaceId: string) => ['workspace-memory', workspaceId] as const,
  document: (workspaceId: string, scope: MemoryScope) =>
    ['workspace-memory', workspaceId, 'document', scope] as const,
  blocks: (workspaceId: string, scope: MemoryScope) =>
    ['workspace-memory', workspaceId, 'blocks', scope] as const,
  revisions: (workspaceId: string, scope: MemoryScope) =>
    ['workspace-memory', workspaceId, 'revisions', scope] as const,
  checkRuns: (workspaceId: string, blockId: string) =>
    ['workspace-memory', workspaceId, 'check-runs', blockId] as const,
  pendingProposals: (workspaceId: string) =>
    ['workspace-memory', workspaceId, 'proposals', 'pending'] as const,
  dismissal: (workspaceId: string) =>
    ['workspace-memory', workspaceId, 'dismissal'] as const,
};

export function memoryDocumentQueryOptions(
  workspaceId: string | null,
  scope: MemoryScope,
) {
  return queryOptions({
    queryKey: memoryKeys.document(workspaceId ?? '', scope),
    queryFn: () => fetchMemoryDocument(workspaceId as string, scope),
    enabled: workspaceId !== null,
    staleTime: 30_000,
  });
}

export function memoryBlocksQueryOptions(
  workspaceId: string | null,
  scope: MemoryScope,
) {
  return queryOptions({
    queryKey: memoryKeys.blocks(workspaceId ?? '', scope),
    queryFn: () => fetchMemoryBlocks(workspaceId as string, scope),
    enabled: workspaceId !== null,
    staleTime: 30_000,
  });
}

export function memoryRevisionsQueryOptions(
  workspaceId: string | null,
  scope: MemoryScope,
) {
  return queryOptions({
    queryKey: memoryKeys.revisions(workspaceId ?? '', scope),
    queryFn: () => fetchMemoryRevisions(workspaceId as string, scope),
    enabled: workspaceId !== null,
    staleTime: 30_000,
  });
}

/**
 * One block's check runs. Read on demand — by the history a person opened, and by a failed row that has
 * to say when the last successful check was — never for every block on a page.
 */
export function memoryCheckRunsQueryOptions(
  workspaceId: string | null,
  blockId: string | null,
) {
  return queryOptions({
    queryKey: memoryKeys.checkRuns(workspaceId ?? '', blockId ?? ''),
    queryFn: () =>
      fetchMemoryCheckRuns(workspaceId as string, blockId as string),
    enabled: workspaceId !== null && blockId !== null,
    staleTime: 30_000,
  });
}

/**
 * The inbox, and the sidebar counter with it — one query for both, so the number on the row and the
 * list behind it can never disagree. Refreshed on the `memory.proposal` push, not by polling.
 */
export function pendingMemoryProposalsQueryOptions(workspaceId: string | null) {
  return queryOptions({
    queryKey: memoryKeys.pendingProposals(workspaceId ?? ''),
    queryFn: () => fetchPendingMemoryProposals(workspaceId as string),
    enabled: workspaceId !== null,
    staleTime: 30_000,
  });
}

/**
 * Put a document the server just answered with into the cache before anything is invalidated.
 *
 * The editor reads `version` off this cache the moment a write lands, and leaving that to an async
 * refetch lets the next save go out with the version it has already superseded — a conflict the person
 * did nothing to cause. Null means the write moved no document, and nothing is seeded.
 */
function seedMemoryDocument(
  queryClient: ReturnType<typeof useQueryClient>,
  workspaceId: string,
  document: MemoryDocument | null,
): void {
  if (document === null) return;
  queryClient.setQueryData(
    memoryKeys.document(workspaceId, document.scope),
    document,
  );
}

/** Everything a write touches: the document, its blocks, its history and the inbox. */
function invalidateMemory(
  queryClient: ReturnType<typeof useQueryClient>,
  workspaceId: string,
): void {
  void queryClient.invalidateQueries({ queryKey: memoryKeys.all(workspaceId) });
}

export function useSaveMemoryDocument(
  workspaceId: string,
): UseMutationResult<MemoryDocument, Error, SaveMemoryDocumentInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SaveMemoryDocumentInput) =>
      saveMemoryDocument(workspaceId, input),
    onSuccess: (document) => {
      /**
       * Seed the cache with what came back before invalidating. The editor re-reads `version` the
       * moment a save lands, and leaving that to an async refetch would let the next save go out with
       * the version it just superseded — a conflict the person did nothing to cause.
       */
      queryClient.setQueryData(
        memoryKeys.document(workspaceId, document.scope),
        document,
      );
      invalidateMemory(queryClient, workspaceId);
    },
  });
}

export function useCreateMemoryProposal(
  workspaceId: string,
): UseMutationResult<FiledMemoryProposal, Error, CreateMemoryProposalInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateMemoryProposalInput) =>
      createMemoryProposal(workspaceId, input),
    onSuccess: (filed) => {
      seedMemoryDocument(queryClient, workspaceId, filed.document);
      invalidateMemory(queryClient, workspaceId);
    },
  });
}

export function useAcceptMemoryProposal(
  workspaceId: string,
): UseMutationResult<
  AcceptedMemoryProposal,
  Error,
  { proposalId: string; scope: MemoryScope; content?: string }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      proposalId: string;
      scope: MemoryScope;
      content?: string;
    }) =>
      acceptMemoryProposal(
        workspaceId,
        input.proposalId,
        input.scope,
        input.content,
      ),
    /**
     * On settled rather than on success, because a refusal changes the server's mind too: a proposal
     * built on a version the document has since left is answered 409 **and moved to `conflicted`**
     * (canon §19). Refetching only when the answer was yes leaves that one sitting in the inbox as a
     * card whose buttons do nothing, which is exactly how it looked.
     */
    onSettled: () => invalidateMemory(queryClient, workspaceId),
  });
}

export function useRejectMemoryProposal(
  workspaceId: string,
): UseMutationResult<
  MemoryProposal,
  Error,
  { proposalId: string; scope: MemoryScope }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { proposalId: string; scope: MemoryScope }) =>
      rejectMemoryProposal(workspaceId, input.proposalId, input.scope),
    /** Keep every inbox reader current, including a tab reopened before refetch finishes. */
    onSuccess: async (proposal) => {
      const queryKey = memoryKeys.pendingProposals(workspaceId);
      /** An older in-flight response must not restore the pending row after it is removed. */
      await queryClient.cancelQueries({ queryKey });
      queryClient.setQueryData<MemoryProposal[]>(queryKey, (previous) =>
        previous?.filter((pending) => pending.id !== proposal.id),
      );
    },
    /** Settled, not success — see {@link useAcceptMemoryProposal}. */
    onSettled: () => invalidateMemory(queryClient, workspaceId),
  });
}

/**
 * The rollback write. The input is named `restoreRevisionId` rather than `revisionId` so a call site
 * has to say which revision it means — see {@link rollbackMemoryRevision} for why the two readings are
 * not the same one.
 */
export function useRollbackMemoryRevision(
  workspaceId: string,
): UseMutationResult<MemoryDocument, Error, { restoreRevisionId: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { restoreRevisionId: string }) =>
      rollbackMemoryRevision(workspaceId, input.restoreRevisionId),
    onSuccess: () => invalidateMemory(queryClient, workspaceId),
  });
}

/**
 * Answer a proposal this same person just filed — unless the server already answered it.
 *
 * The server applies a `personal` proposal in the filing call (canon §8.1), so it comes back `applied`
 * and there is nothing left to accept. Accepting it anyway is answered **409 already applied**, which is
 * the whole reason this is a function and not two lines repeated at both call sites. A `shared` proposal
 * comes back `pending` and is accepted here, which is what makes the button honest for somebody who
 * could have approved it anyway.
 *
 * The status is read off the answer rather than derived from the scope: the server owns which lane it
 * took, and a client that guesses is a client that breaks the day the rule moves.
 */
async function applyFiledProposal(
  workspaceId: string,
  scope: MemoryScope,
  filed: FiledMemoryProposal,
): Promise<MemoryProposal> {
  if (filed.proposal.status === 'applied') return filed.proposal;
  const accepted = await acceptMemoryProposal(
    workspaceId,
    filed.proposal.id,
    scope,
  );
  return accepted.proposal;
}

/**
 * The visibility card's third answer: keep nothing.
 *
 * The request to share is rejected and the fact itself is discarded — a `delete` proposal against the
 * block, answered in the same breath, because filing is the only write lane there is (canon §8.1) and
 * the owner of a personal block is its own approver. Same pair as {@link useAddMemoryBlock}, pointed
 * the other way. Against a personal document the server applies the delete as it files it, and
 * {@link applyFiledProposal} is what keeps this from accepting it a second time.
 *
 * The rejection goes first on purpose. If the delete then fails, the person is left with a private
 * fact and an answered card, which is the harmless direction; deleting first would leave a pending
 * card naming a block that is gone.
 */
export function useDiscardPersonalFact(
  workspaceId: string,
): UseMutationResult<
  MemoryProposal,
  Error,
  { proposalId: string; scope: MemoryScope; blockId: string }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      proposalId: string;
      scope: MemoryScope;
      blockId: string;
    }) => {
      await rejectMemoryProposal(workspaceId, input.proposalId, input.scope);
      const filed = await createMemoryProposal(workspaceId, {
        scope: input.scope,
        operation: 'delete',
        targetBlockId: input.blockId,
        reason: 'The owner discarded this fact instead of sharing it.',
      });
      return applyFiledProposal(workspaceId, input.scope, filed);
    },
    /** Settled, not success — see {@link useAcceptMemoryProposal}. */
    onSettled: () => invalidateMemory(queryClient, workspaceId),
  });
}

/**
 * What the header's **Add memory** form does: file the block as a proposal, then answer it in the same
 * breath when this person is allowed to.
 *
 * Filing is the only write lane there is — there is no `POST /blocks` — and it is the lane the agent's
 * `remember` tool uses too (canon §8.1). Auto-answering keeps the button honest for the person who
 * could have approved it anyway: an editor adding a shared fact expects it in memory, not in a queue
 * they then have to visit. A `viewer` cannot answer, so their block stays filed for review, which is
 * exactly what canon §4 says happens.
 *
 * A personal entry needs no second call at all — the server applies it as it files it — so the accept
 * goes out only for what is genuinely still waiting (see {@link applyFiledProposal}).
 */
export function useAddMemoryBlock(
  workspaceId: string,
): UseMutationResult<MemoryProposal, Error, AddMemoryBlockInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: AddMemoryBlockInput) => {
      const { confirm, ...proposal } = input;
      const filed = await createMemoryProposal(workspaceId, proposal);
      if (!confirm) return filed.proposal;
      return applyFiledProposal(workspaceId, proposal.scope, filed);
    },
    onSuccess: () => invalidateMemory(queryClient, workspaceId),
  });
}

/**
 * The permanent delete: file it, and answer it in the same breath when this person is allowed to.
 *
 * The mirror of {@link useAddMemoryBlock}, pointed the other way, and for the same reason — filing is
 * the only write lane there is (canon §8.1), so a delete is a proposal whether or not anybody else will
 * ever see it. Somebody who may approve gets their memory gone when they press the button, rather than a
 * card in their own inbox asking them what they just asked for; a `viewer` who may edit a block without
 * approving it (canon §4) leaves it filed for an editor, which is the case the two-step exists for.
 *
 * Whether the server already applied it is read off the answer's status by {@link applyFiledProposal},
 * never guessed from the scope or from whether a document came back.
 */
export function useDeleteMemoryBlock(
  workspaceId: string,
): UseMutationResult<MemoryProposal, Error, DeleteMemoryBlockInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: DeleteMemoryBlockInput) => {
      const { confirm, blockId, ...rest } = input;
      const filed = await createMemoryProposal(workspaceId, {
        ...rest,
        operation: 'delete',
        targetBlockId: blockId,
      });
      if (!confirm) return filed.proposal;
      return applyFiledProposal(workspaceId, rest.scope, filed);
    },
    /**
     * Settled, not success: this writes twice. A filing that lands and an accept that then refuses
     * leaves a real proposal on the server — pending, or conflicted — and a section still showing the
     * memory as though nothing had happened. The refusal is the case that most needs the refresh.
     */
    onSettled: () => invalidateMemory(queryClient, workspaceId),
  });
}

/** What a permanent delete sends: which block, why, and whether to answer it in the same breath. */
export interface DeleteMemoryBlockInput {
  scope: MemoryScope;
  blockId: string;
  reason: string;
  confirm: boolean;
}

/**
 * What the **Add memory** form sends: a proposal, plus whether to answer it in the same breath.
 *
 * The revision condition rides the proposal itself (see {@link CreateMemoryProposalInput}), so nothing
 * here depends on `confirm` — a `viewer` filing a `decision` for review sends the condition with it and
 * it lands when somebody says yes.
 */
export interface AddMemoryBlockInput extends CreateMemoryProposalInput {
  confirm: boolean;
}

/**
 * The patch write: a block's text, type, section, visibility, status, or revision condition.
 *
 * Both the block and the document come back, and both are seeded into the cache before the
 * invalidation — a patch that takes a block out of the text moves the document's version, and the
 * editor beside it has to be on the version the server is actually on.
 */
export function usePatchMemoryBlock(
  workspaceId: string,
): UseMutationResult<
  PatchedMemoryBlock,
  Error,
  { blockId: string } & PatchMemoryBlockInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { blockId: string } & PatchMemoryBlockInput) => {
      const { blockId, ...patch } = input;
      return patchMemoryBlock(workspaceId, blockId, patch);
    },
    onSuccess: (patched) => {
      queryClient.setQueryData(
        memoryKeys.document(workspaceId, patched.document.scope),
        patched.document,
      );
      invalidateMemory(queryClient, workspaceId);
    },
  });
}
